"use client";
import React, { createContext, useContext, ReactNode } from "react";
import { ToastContainer } from "@/components/ui/Toast";
import { useToast } from "@/hooks/useToast";

interface ToastContextType {
  success: (title: string, options?: { message?: string; duration?: number }) => string;
  error: (title: string, options?: { message?: string; duration?: number }) => string;
  warning: (title: string, options?: { message?: string; duration?: number }) => string;
  info: (title: string, options?: { message?: string; duration?: number }) => string;
  clearAll: () => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const { toasts, removeToast, success, error, warning, info, clearAllToasts } = useToast();

  const contextValue: ToastContextType = {
    success,
    error,
    warning,
    info,
    clearAll: clearAllToasts,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToastContext must be used within a ToastProvider");
  }
  return context;
};

export default ToastProvider;
